import { useEffect, type RefObject } from "react";

interface Options {
	/** Whether the modal is open. Effects are skipped when false. */
	open: boolean;
	/** Called when Escape is pressed (before sub-modal check, if provided). */
	onClose: () => void;
	/** Modal container ref for focus trap. */
	modalRef: RefObject<HTMLElement | null>;
	/** If true, Escape closes this sub-modal first instead of calling onClose. */
	hasSubModal?: boolean;
	/** Called to close the sub-modal. Required when hasSubModal is true. */
	onCloseSubModal?: () => void;
	/**
	 * Auto-focus the first field on open (default true). Set false for forms that
	 * validate on blur (mode: 'onTouched'): auto-focusing a required field gets it
	 * blurred during the modal's mount/re-render churn, which marks it touched and
	 * fires validation before the user has typed anything.
	 */
	autoFocusFirstField?: boolean;
}

/**
 * Shared modal effects: body scroll lock, Escape key handling, focus trap.
 */
export function useModalEffects({
	open,
	onClose,
	modalRef,
	hasSubModal = false,
	onCloseSubModal,
	autoFocusFirstField = true,
}: Options) {
	// ── Body scroll lock ──
	useEffect(() => {
		if (!open) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [open]);

	// ── Escape key ──
	useEffect(() => {
		if (!open) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key !== "Escape") return;
			if (hasSubModal && onCloseSubModal) {
				onCloseSubModal();
			} else {
				onClose();
			}
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [open, onClose, hasSubModal, onCloseSubModal]);

	// ── Focus trap ──
	useEffect(() => {
		if (!open) return;
		const el = modalRef.current;
		if (!el) return;

		const focusable =
			'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

		// Auto-focus first field (skipped for blur-validated forms — see autoFocusFirstField).
		if (autoFocusFirstField) {
			const firstInput = el.querySelector<HTMLElement>(
				'input:not([type="radio"]):not([type="hidden"])',
			);
			firstInput?.focus();
		}

		const trap = (e: KeyboardEvent) => {
			if (e.key !== "Tab") return;
			const nodes = el.querySelectorAll<HTMLElement>(focusable);
			if (nodes.length === 0) return;
			const first = nodes[0];
			const last = nodes[nodes.length - 1];
			if (e.shiftKey) {
				if (document.activeElement === first) {
					e.preventDefault();
					last.focus();
				}
			} else if (document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", trap);
		return () => document.removeEventListener("keydown", trap);
	}, [open, modalRef, autoFocusFirstField]);
}
